<template>
  <div>
    <Modal @close="setDisplayExpiryModal(false)" :showModal="displayExpiryModal" size="modal-md" data-cy="token-expiry-modal" :closeable="false">
      <template v-slot:header>
        <slot name="header">
          <span class="d-block mb-3 text-center">
            You will be logged out soon.
          </span>
        </slot>
      </template>
      <template v-slot:body>
        <slot name="body">
          <div class="mb-5">
            <p class="text-center">
              For your security, we will log you out automatically when you haven't been active for a certain period of time.
            </p>
          </div>
          <div class="h3 text-center mb-5">
            {{ formattedMinutes }}:{{ formattedSeconds }}
          </div>
        </slot>
      </template>
      <template v-slot:footer>
        <slot name="footer">
          <div class="modal-footer d-flex flex-md-row-reverse flex-column flex-md-row flex-nowrap p-3 justify-content-center">
            <button
              type="button"
              data-bs-dismiss="modal"
              data-cy="expiryContinueBtn"
              @click="extendSession()"
              :disabled="buttonsDisabled"
              class="btn text-decoration-none fw-bold close-button btn-primary m-0 ms-md-2 mb-3 mb-md-0 w-100"
            >
              Continue session
            </button>
            <button
              type="button"
              data-bs-dismiss="modal"
              data-cy="expiryLogoutBtn"
              @click="handleLogoutClick()"
              :disabled="buttonsDisabled"
              class="btn text-decoration-none fw-bold close-button btn-outline-primary m-0 me-md-2 w-100"
            >
              <ButtonLoadingSpinner v-if="buttonsDisabled"></ButtonLoadingSpinner>
              Log out now
            </button>
          </div>
        </slot>
      </template>
    </Modal>
  </div>
</template>

<script>
import { storeToRefs } from 'pinia'
import Modal from "~/components/modal.vue";
import { useRootStore } from '~/store/root'
import { useUserStore } from '~/store/user'
import { useAppAnalyticsStore } from '~/store/app-analytics'
import IntervalWorker from '~/src/workers/tokenExpiryInterval?worker&inline'

export default {
  name: "TokenExpiryModal",
  async setup() {
    const { displayExpiryModal, authTokenExpiry} = storeToRefs(useRootStore())
    const { setDisplayExpiryModal, logout, setupTokenExpirationTimeout } = useRootStore()
    const { fsTrackEvent } = useAppAnalyticsStore()
    const { reauth } = useUserStore()
    const route = useRoute()

    return {
      displayExpiryModal,
      authTokenExpiry,
      setDisplayExpiryModal,
      fsTrackEvent,
      logout,
      route,
      reauth,
      setupTokenExpirationTimeout,
    }
  },
  data () {
    return {
      timeDiff: {},
      intervalWorker: null,
      buttonsDisabled: false
    }
  },
  emits: ["close"],
  components: {
    Modal,
  },
  computed: {
    formattedMinutes() {
      const minutes = this.timeDiff.minutes ?? ''
      const padSymbol = minutes || minutes === 0 ? '0' : '-'
      return minutes.toString().padStart(2, padSymbol)
    },
    formattedSeconds() {
      const seconds = this.timeDiff.seconds ?? ''
      const padSymbol = seconds || seconds === 0 ? '0' : '-'
      return seconds.toString().padStart(2, padSymbol)
    },
  },
  methods: {
    fullstoryEvent(eventStr) {
      this.fsTrackEvent({ name: 'Token Expiry Modal', properties: { event: eventStr } })
    },
    handleLogoutClick() {
      this.fullstoryEvent('user-logout')
      this.userLogout()
    },
    userLogout() {
      this.buttonsDisabled = true
      this.resetData()
      this.logout({ path: this.route.path })
      this.setDisplayExpiryModal(false)
    },
    async extendSession(){
      this.buttonsDisabled = true
      this.fullstoryEvent('extend-session')
      this.reauth()
      this.setupTokenExpirationTimeout({ delayStart: 30 })
      this.setDisplayExpiryModal(false)
    },
    resetData() {
      if (this.intervalWorker) {
        this.intervalWorker.terminate()
      }
      this.timeDiff = {}
      this.intervalWorker = null
    },
    startTimer() {
      this.intervalWorker = new IntervalWorker()
      this.intervalWorker.onmessage = ({ data }) => {
        this.timeDiff = data
        if (this.timeDiff.minutes <= 0 && this.timeDiff.seconds <= 0) {
          this.fullstoryEvent('timeout-logout')
          this.userLogout()
        }
      }
      this.intervalWorker.postMessage(this.authTokenExpiry)
    }
  },
  beforeUnmount() {
    if (this.intervalWorker) {
      this.intervalWorker.terminate()
    }
  },
  watch: {
    displayExpiryModal (newVal) {
      this.resetData()
      if (newVal) {
        this.fullstoryEvent('opened')
        this.startTimer()
        this.buttonsDisabled = false
      }
    },
  }
};
</script>

<style scoped lang="scss">
</style>
